/**
 * Security Interests Panel — unified data table with filter tabs
 * (All / PPSR Registered / Unperfected), flag badges, search, party type
 * filter, and detail slide-out.
 *
 * Reads canonical entries from the `securityInterestEntries` table.
 * Updates incrementally as the agent writes entries.
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { toast } from "sonner";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Checkbox } from "@/ui/primitives/checkbox";
import { BulkActionBar, BulkActionButton } from "@/ui/composed/bulk-action-bar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import {
  Loader2,
  Shield,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  Plus,
} from "lucide-react";
import { cn } from "@/utils/misc";
import { usePersistedSet } from "@/utils/use-persisted-set";
import type { SecurityInterestEntryUI } from "./types";
import { SecurityInterestDetailPanel } from "./SecurityInterestDetailPanel";
import { BulkValuationDialog } from "../shared-panels/BulkValuationDialog";
import { useFlagDismissals } from "../flags";
import {
  AddSecurityInterestPanel,
  type AddSecurityInterestPrefill,
} from "./AddSecurityInterestPanel";
import {
  useLinkCounts,
  useLinkedRecordStack,
} from "../shared-panels/linkShared";
import { LinkIndicator } from "../shared-panels/LinkIndicator";
import { SecondaryDetailPanel } from "../shared-panels/SecondaryDetailPanel";
import {
  useNewOutputBoundary,
  isNewOutput,
} from "../../../_shared/use-new-output-boundary";
import { NewOutputBadge } from "../../../_shared/new-output-badge";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const COLLATERAL_CLASS_LABELS: Record<string, string> = {
  motor_vehicle: "Motor vehicle",
  allpap_no_exceptions: "ALLPAP (no exceptions)",
  allpap_with_exceptions: "ALLPAP (with exceptions)",
  other_goods: "Other goods",
  account: "Account",
  intangible_property: "Intangible property",
  financial_property: "Financial property",
  chattel_paper: "Chattel paper",
  general_intangible: "General intangible",
  inventory: "Inventory",
  negotiable_instrument: "Negotiable instrument",
};

function fmtDate(d?: string): string {
  if (!d) return "\u2014";
  try {
    return new Date(d).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
}

const AUD = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  maximumFractionDigits: 0,
});

function fmtAud(amount?: number): string {
  if (amount == null) return "\u2014";
  return AUD.format(amount);
}

const OWED_SOURCE_LABEL: Record<string, string> = {
  proof_of_debt: "via PoD",
  document: "via doc",
  manual: "Manual",
};

function OwedCell({ entry }: { entry: SecurityInterestEntryUI }) {
  if (entry.owedAmount == null) {
    return <span className="text-xs text-muted-foreground/40">{"\u2014"}</span>;
  }
  // The source is only set when the figure comes from a specific estimate; the
  // auto rollup from linked debts leaves it blank and so shows no label.
  const sourceLabel = entry.owedAmountSource
    ? OWED_SOURCE_LABEL[entry.owedAmountSource]
    : undefined;
  return (
    <div className="flex min-w-0 flex-col items-end gap-0.5">
      <span className="text-sm font-medium text-foreground">
        {fmtAud(entry.owedAmount)}
      </span>
      {(sourceLabel || entry.owedAmountAsAt) && (
        <span className="truncate text-[10px] text-muted-foreground">
          {sourceLabel ?? ""}
          {sourceLabel && entry.owedAmountAsAt ? " · " : ""}
          {entry.owedAmountAsAt ? fmtDate(entry.owedAmountAsAt) : ""}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat card
// ---------------------------------------------------------------------------

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: number | string;
  accent?: string;
}) {
  return (
    <div className="min-w-0 rounded-xl border bg-muted/30 px-4 py-3">
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          "mt-0.5 truncate text-2xl font-semibold",
          accent ?? "text-foreground",
        )}
      >
        {value}
      </div>
    </div>
  );
}

const AUD_COMPACT = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  notation: "compact",
  maximumFractionDigits: 1,
});

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function SecurityInterestsPanel({
  jobId,
  taskId,
  isRunning,
  isViewer,
  compact = false,
}: {
  jobId: Id<"jobs">;
  taskId: Id<"summarizationTasks">;
  isRunning: boolean;
  isViewer?: boolean;
  /**
   * When `true`, hides the stat-cards row (Registered / Flagged /
   * Related party / Unperfected / Total owed) and the "Add Security
   * Interest" button so the panel renders as table-only. Used by the
   * Overview (Plan) page.
   */
  compact?: boolean;
}) {
  const [activeTab, setActiveTab] = useState<
    "all" | "registered" | "unperfected" | "excluded"
  >("all");
  const [filterFlagged, setFilterFlagged] = useState(false);
  const [partyFilter, setPartyFilter] = useState<
    "all" | "related_party" | "third_party"
  >("all");
  const [collateralFilter, setCollateralFilter] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const [addPanelOpen, setAddPanelOpen] = useState(false);
  // Bulk multi-select — scoped to the current tab (cleared on tab change).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkValuationOpen, setBulkValuationOpen] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  const bulkToggleExcluded = useMutation(
    api.jobs.summarization.agents.securityInterests.publicMutations
      .bulkToggleExcluded,
  );
  const bulkAddOwedEstimate = useMutation(
    api.jobs.summarization.agents.securityInterests.publicMutations
      .bulkAddOwedEstimate,
  );

  // Stack of side-by-side linked-record panels so the user can chase a chain
  // of related records without losing context.
  const linkedStack = useLinkedRecordStack(
    selectedEntryId ? { type: "security_interest", id: selectedEntryId } : null,
  );
  const [addPrefill, setAddPrefill] = useState<
    AddSecurityInterestPrefill | undefined
  >(undefined);

  const openAddPanel = (prefill?: AddSecurityInterestPrefill) => {
    setAddPrefill(prefill);
    setAddPanelOpen(true);
  };

  const flagCtx = useFlagDismissals(jobId);
  const linkCounts = useLinkCounts(jobId);
  const newSince = useNewOutputBoundary(jobId, "sum_security_interests");

  const { data: allEntries } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listSecurityInterestEntriesForJob,
      { jobId, includesMerged: true },
    ),
  );

  const entries = useMemo(() => allEntries ?? [], [allEntries]);

  const activeEntries = useMemo(
    () => entries.filter((e) => e.status === "active"),
    [entries],
  );

  // Excluded security interests stay in the register (Excluded tab) but are
  // dropped from every total, secured netting, the waterfall, POD and
  // reporting. Everything except the Excluded tab works off nonExcludedActive.
  const nonExcludedActive = useMemo(
    () => activeEntries.filter((e) => !e.isExcluded),
    [activeEntries],
  );
  const excludedActive = useMemo(
    () => activeEntries.filter((e) => e.isExcluded),
    [activeEntries],
  );

  // If the last excluded row is re-included while viewing the Excluded tab,
  // fall back to All so the practitioner isn't left on an empty tab.
  useEffect(() => {
    if (activeTab === "excluded" && excludedActive.length === 0) {
      setActiveTab("all");
    }
  }, [activeTab, excludedActive.length]);

  // Stats
  const stats = useMemo(() => {
    const registered = nonExcludedActive.filter(
      (e) => e.registrationStatus === "registered",
    );
    const unperfected = nonExcludedActive.filter(
      (e) => e.registrationStatus === "unperfected",
    );
    const flagged = nonExcludedActive.filter((e) => e.flags.length > 0);
    const related = nonExcludedActive.filter(
      (e) => e.partyType === "related_party",
    );
    const totalOwed = nonExcludedActive.reduce(
      (sum, e) => sum + (typeof e.owedAmount === "number" ? e.owedAmount : 0),
      0,
    );
    return {
      totalRegistered: registered.length,
      totalUnperfected: unperfected.length,
      totalFlagged: flagged.length,
      totalRelated: related.length,
      totalOwed,
    };
  }, [nonExcludedActive]);

  // Filtered entries (unified across tabs)
  const filteredEntries = useMemo(() => {
    const base = activeTab === "excluded" ? excludedActive : nonExcludedActive;
    const predicates: Array<(e: (typeof base)[number]) => boolean> = [];
    if (activeTab === "registered")
      predicates.push((e) => e.registrationStatus === "registered");
    if (activeTab === "unperfected")
      predicates.push((e) => e.registrationStatus === "unperfected");
    if (filterFlagged) predicates.push((e) => e.flags.length > 0);
    if (partyFilter !== "all")
      predicates.push((e) => e.partyType === partyFilter);
    if (collateralFilter)
      predicates.push((e) => e.collateralType === collateralFilter);
    if (classFilter) predicates.push((e) => e.collateralClass === classFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      predicates.push(
        (e) =>
          e.securedParty.name.toLowerCase().includes(q) ||
          (e.ppsrRegistrationNumber ?? "").includes(q) ||
          e.collateralDescription.toLowerCase().includes(q),
      );
    }
    return base.filter((e) => predicates.every((p) => p(e)));
  }, [
    nonExcludedActive,
    excludedActive,
    activeTab,
    filterFlagged,
    partyFilter,
    collateralFilter,
    classFilter,
    search,
  ]);

  const hasFilters = partyFilter !== "all" || collateralFilter || classFilter;

  // -------------------------------------------------------------------------
  // Bulk selection (checkbox column + floating action bar)
  // -------------------------------------------------------------------------

  // Selection is scoped to the current tab, so clear it on tab change.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeTab]);

  const selectionEnabled = !isViewer;
  const visibleIds = useMemo(
    () => filteredEntries.map((e) => e._id),
    [filteredEntries],
  );
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id));

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allSelected =
        visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(visibleIds);
    });
  }, [visibleIds]);

  // On the Excluded tab the selection is already excluded → re-include;
  // everywhere else → exclude.
  const bulkExcludeIntent = activeTab !== "excluded";

  const handleBulkExclude = useCallback(async () => {
    const ids = [...selectedIds] as Id<"securityInterestEntries">[];
    if (ids.length === 0) return;
    try {
      const res = await bulkToggleExcluded({
        entryIds: ids,
        isExcluded: bulkExcludeIntent,
      });
      toast.success(
        `${bulkExcludeIntent ? "Excluded" : "Re-included"} ${res.updated} security interest${
          res.updated === 1 ? "" : "s"
        }`,
      );
      setSelectedIds(new Set());
    } catch {
      toast.error(
        "Couldn't update the selected security interests. Please try again.",
      );
    }
  }, [selectedIds, bulkExcludeIntent, bulkToggleExcluded]);

  const handleBulkValuation = useCallback(
    async (input: { amount: number; asAtDate?: string; notes?: string }) => {
      const ids = [...selectedIds] as Id<"securityInterestEntries">[];
      if (ids.length === 0) return;
      setBulkSubmitting(true);
      try {
        const res = await bulkAddOwedEstimate({
          entryIds: ids,
          amount: input.amount,
          asAtDate: input.asAtDate,
          notes: input.notes,
        });
        toast.success(
          `Updated owed amount on ${res.updated} security interest${
            res.updated === 1 ? "" : "s"
          }`,
        );
        setBulkValuationOpen(false);
        setSelectedIds(new Set());
      } catch {
        toast.error("Couldn't update the owed amounts. Please try again.");
      } finally {
        setBulkSubmitting(false);
      }
    },
    [selectedIds, bulkAddOwedEstimate],
  );

  // Group by secured party name
  const groups = useMemo(() => {
    const map = new Map<string, SecurityInterestEntryUI[]>();
    for (const entry of filteredEntries) {
      const key = entry.securedParty.name;
      const list = map.get(key);
      if (list) {
        list.push(entry);
      } else {
        map.set(key, [entry]);
      }
    }
    return Array.from(map.entries())
      .map(([name, groupEntries]) => ({
        name,
        entries: groupEntries,
        totalOwed: groupEntries.reduce(
          (sum, e) =>
            sum +
            (e.isExcluded || typeof e.owedAmount !== "number"
              ? 0
              : e.owedAmount),
          0,
        ),
      }))
      .sort((a, b) => b.entries.length - a.entries.length);
  }, [filteredEntries]);

  const hasMultipleGroups =
    groups.length > 1 || (groups.length === 1 && groups[0].entries.length > 1);

  // Collapse / expand — MOR-626: collapsed by default; the user's expand/collapse
  // choice persists globally to localStorage (shared with the overview view).
  const [expandedGroups, setExpandedGroups] = usePersistedSet(
    "security-interests:expanded-groups",
  );

  const toggleGroup = (name: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const allGroupNames = useMemo(
    () => groups.filter((g) => g.entries.length > 1).map((g) => g.name),
    [groups],
  );

  const allExpanded =
    allGroupNames.length > 0 &&
    allGroupNames.every((n) => expandedGroups.has(n));

  const toggleAll = () => {
    if (allExpanded) setExpandedGroups(new Set());
    else setExpandedGroups(new Set(allGroupNames));
  };

  const selectedEntry = useMemo(
    () => entries.find((e) => e._id === selectedEntryId) ?? null,
    [entries, selectedEntryId],
  );

  const colCount = 7;

  const filteredTotalOwed = useMemo(
    () =>
      filteredEntries.reduce(
        (sum, e) =>
          sum +
          (e.isExcluded || typeof e.owedAmount !== "number" ? 0 : e.owedAmount),
        0,
      ),
    [filteredEntries],
  );

  // Tabs — the Excluded tab only appears once something has been excluded.
  const tabs: Array<{
    key: "all" | "registered" | "unperfected" | "excluded";
    label: string;
    count: number;
  }> = [
    { key: "all", label: "All", count: nonExcludedActive.length },
    {
      key: "registered",
      label: "PPSR Registered",
      count: stats.totalRegistered,
    },
    { key: "unperfected", label: "Unperfected", count: stats.totalUnperfected },
  ];
  if (excludedActive.length > 0) {
    tabs.push({
      key: "excluded",
      label: "Excluded",
      count: excludedActive.length,
    });
  }

  // ---------------------------------------------------------------------------
  // Empty / loading states
  // ---------------------------------------------------------------------------

  if (entries.length === 0) {
    if (isRunning) {
      return (
        <div className="flex flex-col items-center gap-3 py-16">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          <p className="text-sm text-muted-foreground">
            Extracting security interests...
          </p>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center gap-3 py-16">
        <div className="rounded-full bg-muted p-3">
          <Shield className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground">
          No security interest entries yet.
        </p>
        {!isViewer && (
          <>
            <Button
              variant="outline"
              size="sm"
              className="mt-2 gap-1.5"
              onClick={() => openAddPanel()}
            >
              <Plus className="h-3.5 w-3.5" />
              Add Security Interest
            </Button>
            <AddSecurityInterestPanel
              jobId={jobId}
              taskId={taskId}
              open={addPanelOpen}
              onClose={() => setAddPanelOpen(false)}
              onCreated={(id) => {
                setAddPanelOpen(false);
                setSelectedEntryId(id);
              }}
              prefill={addPrefill}
            />
          </>
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {/* Card */}
      <div className="overflow-hidden rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-shadow duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)]">
        {/* Header — Add button (right-aligned, above stats) — hidden in compact mode */}
        {!compact && !isViewer && (
          <div className="flex justify-end px-5 pt-5">
            <Button
              size="sm"
              onClick={() => openAddPanel()}
              className="gap-1.5"
            >
              <Plus className="h-3.5 w-3.5" />
              Add Security Interest
            </Button>
          </div>
        )}

        {/* Stats — hidden in compact mode */}
        {!compact && (
          <div className="grid grid-cols-5 gap-3 px-5 pb-4 pt-4">
            <StatCard label="Registered" value={stats.totalRegistered} />
            <StatCard
              label="Flagged"
              value={stats.totalFlagged}
              accent="text-destructive"
            />
            <StatCard
              label="Related party"
              value={stats.totalRelated}
              accent="text-amber-600 dark:text-amber-400"
            />
            <StatCard
              label="Unperfected"
              value={stats.totalUnperfected}
              accent="text-destructive"
            />
            <StatCard
              label="Total est. owed"
              value={
                stats.totalOwed > 0
                  ? AUD_COMPACT.format(stats.totalOwed)
                  : "\u2014"
              }
            />
          </div>
        )}

        {/* Search */}
        <div className="px-5 pb-3 pt-4">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search parties..."
            className="w-full rounded-lg border bg-background px-3 py-2 text-sm placeholder-muted-foreground transition-colors focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3 px-5 pb-4">
          <FilterSelect
            label="Party type"
            value={partyFilter === "all" ? "" : partyFilter}
            onChange={(v) =>
              setPartyFilter(
                (v || "all") as "all" | "related_party" | "third_party",
              )
            }
            options={[
              { value: "", label: "All" },
              { value: "related_party", label: "Related party" },
              { value: "third_party", label: "Third party" },
            ]}
          />
          <FilterSelect
            label="Collateral"
            value={collateralFilter}
            onChange={setCollateralFilter}
            options={[
              { value: "", label: "All" },
              { value: "commercial_property", label: "Commercial" },
              { value: "consumer_property", label: "Consumer" },
            ]}
          />
          <FilterSelect
            label="Class"
            value={classFilter}
            onChange={setClassFilter}
            options={[
              { value: "", label: "All" },
              ...Object.entries(COLLATERAL_CLASS_LABELS).map(([k, v]) => ({
                value: k,
                label: v,
              })),
            ]}
          />
          {hasFilters && (
            <button
              type="button"
              onClick={() => {
                setPartyFilter("all");
                setCollateralFilter("");
                setClassFilter("");
              }}
              className="pb-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Filter tabs */}
        <div className="flex items-center justify-between border-b border-border px-5">
          <div className="flex gap-0">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  "-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors",
                  activeTab === tab.key
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <label className="flex cursor-pointer select-none items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={filterFlagged}
                onChange={(e) => setFilterFlagged(e.target.checked)}
                className="rounded border-border accent-primary"
              />
              Flagged only
            </label>
            {hasMultipleGroups && allGroupNames.length > 0 && (
              <button
                type="button"
                onClick={toggleAll}
                className="flex items-center gap-1 whitespace-nowrap px-2 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
              >
                <ChevronsUpDown className="h-3 w-3" />
                {allExpanded ? "Collapse all" : "Expand all"}
              </button>
            )}
          </div>
        </div>

        {/* Unified table */}
        <div className="min-w-0 flex-1">
          <div className="overflow-auto">
            <Table className="w-full table-fixed">
              <colgroup>
                <col className="w-[19%]" />
                <col className="w-[9%]" />
                <col className="w-[11%]" />
                <col className="w-[11%]" />
                <col className="w-[15%]" />
                <col className="w-[7%]" />
                <col className="w-[14%]" />
              </colgroup>
              <TableHeader>
                <TableRow className="bg-muted/30 hover:bg-transparent">
                  <TableHead className="h-10 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <div className="flex items-center gap-2.5">
                      {selectionEnabled && (
                        <Checkbox
                          aria-label="Select all security interests"
                          checked={
                            allVisibleSelected
                              ? true
                              : someVisibleSelected
                                ? "indeterminate"
                                : false
                          }
                          onCheckedChange={toggleSelectAll}
                        />
                      )}
                      Secured party
                    </div>
                  </TableHead>
                  <TableHead className="h-10 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Party type
                  </TableHead>
                  <TableHead className="h-10 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Start date
                  </TableHead>
                  <TableHead className="h-10 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    End date
                  </TableHead>
                  <TableHead className="h-10 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Class
                  </TableHead>
                  <TableHead className="h-10 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    PMSI
                  </TableHead>
                  <TableHead className="h-10 pr-6 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Est. owed
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={colCount} className="h-32 text-center">
                      <span className="text-sm text-muted-foreground">
                        {filterFlagged
                          ? "No flagged security interests"
                          : "No results found"}
                      </span>
                    </TableCell>
                  </TableRow>
                ) : (
                  groups.map((group) => {
                    const isSingleEntry = group.entries.length === 1;
                    const isExpanded =
                      isSingleEntry || expandedGroups.has(group.name);

                    // Number of entries in this group that "glow" (unperfected
                    // or carrying active flags). When the group is collapsed
                    // these rows are hidden, so the header must surface the
                    // signal — otherwise the user has no cue to expand and look.
                    const attentionCount = group.entries.filter((entry) => {
                      if (entry.registrationStatus === "unperfected")
                        return true;
                      return (
                        flagCtx.activeCount(
                          "securityInterests",
                          entry._id,
                          entry.flags.map((f) => ({
                            type: f.type,
                            detail: f.detail ?? "",
                          })),
                        ) > 0
                      );
                    }).length;
                    const showCollapsedGlow = !isExpanded && attentionCount > 0;

                    return (
                      <Fragment key={group.name}>
                        {/* Group header row */}
                        {!isSingleEntry && (
                          <TableRow
                            className={cn(
                              "cursor-pointer select-none border-t border-border",
                              showCollapsedGlow
                                ? "bg-red-50/60 hover:bg-red-50/80 dark:bg-red-950/30 dark:hover:bg-red-950/50"
                                : "bg-muted/40 hover:bg-muted/50",
                            )}
                            onClick={() => toggleGroup(group.name)}
                          >
                            <TableCell className="px-4 py-2.5">
                              <div className="flex items-center gap-2">
                                {isExpanded ? (
                                  <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                )}
                                <span className="truncate text-sm font-semibold text-foreground">
                                  {group.name}
                                </span>
                                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-muted-foreground/15 px-1.5 text-[11px] font-semibold tabular-nums text-muted-foreground">
                                  {group.entries.length}
                                </span>
                                {showCollapsedGlow && (
                                  <Badge
                                    color="red"
                                    variant="soft"
                                    size="sm"
                                    className="shrink-0"
                                  >
                                    {attentionCount} flagged
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell colSpan={colCount - 2} />
                            <TableCell className="px-4 py-2.5 pr-6 text-right">
                              {group.totalOwed > 0 && (
                                <span className="text-sm font-bold tabular-nums text-foreground">
                                  {fmtAud(group.totalOwed)}
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Entry rows (hidden when collapsed for multi-entry groups) */}
                        {isExpanded &&
                          group.entries.map((entry) => {
                            const isUnperfected =
                              entry.registrationStatus === "unperfected";
                            const isChildRow = !isSingleEntry;
                            const activeFlags = flagCtx.activeCount(
                              "securityInterests",
                              entry._id,
                              entry.flags.map((f) => ({
                                type: f.type,
                                detail: f.detail ?? "",
                              })),
                            );
                            return (
                              <TableRow
                                key={entry._id}
                                onClick={() => setSelectedEntryId(entry._id)}
                                className={cn(
                                  "cursor-pointer transition-colors",
                                  isChildRow &&
                                    "border-l-2 border-l-primary/30",
                                  isUnperfected
                                    ? "bg-red-50/40 hover:bg-red-50/70 dark:bg-red-950/20 dark:hover:bg-red-950/40"
                                    : activeFlags > 0
                                      ? "bg-red-50/40 hover:bg-red-50/70 dark:bg-red-950/20 dark:hover:bg-red-950/40"
                                      : isChildRow
                                        ? "bg-muted/20 hover:bg-muted/40"
                                        : "hover:bg-muted/50",
                                  selectedEntryId === entry._id &&
                                    "bg-primary/5",
                                )}
                              >
                                <TableCell
                                  className={cn(
                                    "px-4 py-3",
                                    isChildRow && "pl-8",
                                  )}
                                >
                                  <div className="flex items-center gap-2">
                                    {selectionEnabled && (
                                      <span
                                        onClick={(e) => e.stopPropagation()}
                                        className="flex items-center"
                                      >
                                        <Checkbox
                                          aria-label={`Select ${entry.securedParty.name}`}
                                          checked={selectedIds.has(entry._id)}
                                          onCheckedChange={() =>
                                            toggleSelect(entry._id)
                                          }
                                        />
                                      </span>
                                    )}
                                    <div
                                      className={cn(
                                        "truncate text-sm font-medium",
                                        entry.isExcluded
                                          ? "text-muted-foreground line-through"
                                          : "text-foreground",
                                      )}
                                    >
                                      {entry.securedParty.name}
                                    </div>
                                    {isNewOutput(
                                      entry._creationTime,
                                      newSince,
                                    ) && <NewOutputBadge />}
                                    {entry.isExcluded && (
                                      <Badge
                                        color="gray"
                                        variant="soft"
                                        size="sm"
                                        className="shrink-0"
                                      >
                                        Excluded
                                      </Badge>
                                    )}
                                    {isUnperfected && (
                                      <Badge
                                        color="red"
                                        variant="soft"
                                        size="sm"
                                        className="shrink-0"
                                      >
                                        Unperfected
                                      </Badge>
                                    )}
                                    <LinkIndicator
                                      count={
                                        linkCounts.bySecurityInterest.get(
                                          entry._id,
                                        ) ?? 0
                                      }
                                    />
                                  </div>
                                  <div className="truncate font-mono text-[10px] text-muted-foreground">
                                    {entry.securedParty.identifierType !==
                                      "unknown" &&
                                      entry.securedParty.identifierType}{" "}
                                    {entry.securedParty.identifier}
                                  </div>
                                </TableCell>
                                <TableCell className="px-4 py-3">
                                  <Badge
                                    color={
                                      entry.partyType === "related_party"
                                        ? "amber"
                                        : "blue"
                                    }
                                    variant="soft"
                                    size="sm"
                                  >
                                    {entry.partyType === "related_party"
                                      ? "Related"
                                      : "Third party"}
                                  </Badge>
                                </TableCell>
                                <TableCell className="px-4 py-3 text-sm tabular-nums text-muted-foreground">
                                  {fmtDate(entry.registrationStartDate)}
                                </TableCell>
                                <TableCell className="px-4 py-3 text-sm tabular-nums text-muted-foreground">
                                  {fmtDate(entry.registrationEndDate)}
                                </TableCell>
                                <TableCell className="truncate px-4 py-3 text-xs text-muted-foreground">
                                  {COLLATERAL_CLASS_LABELS[
                                    entry.collateralClass
                                  ] ?? entry.collateralClass}
                                </TableCell>
                                <TableCell className="px-4 py-3 text-center text-sm text-muted-foreground">
                                  {entry.pmsi ? "Yes" : "No"}
                                </TableCell>
                                <TableCell className="px-4 py-3 pr-6 text-right tabular-nums">
                                  <OwedCell entry={entry} />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </Fragment>
                    );
                  })
                )}

                {/* Total row */}
                {filteredEntries.length > 0 && (
                  <TableRow className="border-t-2 bg-muted/20 hover:bg-muted/20">
                    <TableCell className="px-4 py-3 text-sm font-bold text-muted-foreground">
                      Total ({filteredEntries.length} registration
                      {filteredEntries.length !== 1 ? "s" : ""})
                    </TableCell>
                    <TableCell colSpan={colCount - 2} />
                    <TableCell className="px-4 py-3 pr-6 text-right">
                      <span className="text-sm font-bold tabular-nums text-foreground">
                        {filteredTotalOwed > 0
                          ? fmtAud(filteredTotalOwed)
                          : "\u2014"}
                      </span>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Detail panel */}
      <SecurityInterestDetailPanel
        entry={selectedEntry}
        open={selectedEntry !== null}
        onClose={() => {
          setSelectedEntryId(null);
          linkedStack.closeAll();
        }}
        onDeleted={() => {
          setSelectedEntryId(null);
          linkedStack.closeAll();
        }}
        isViewer={isViewer}
        flagCtx={flagCtx}
        onOpenLinked={linkedStack.open}
      />

      {/* Stack of side-by-side linked records */}
      <SecondaryDetailPanel
        jobId={jobId}
        stack={linkedStack.stack}
        onCloseAt={linkedStack.closeAt}
        onOpenLinked={linkedStack.open}
        isViewer={isViewer}
      />

      {/* Add Security Interest panel */}
      {!isViewer && (
        <AddSecurityInterestPanel
          jobId={jobId}
          taskId={taskId}
          open={addPanelOpen}
          onClose={() => setAddPanelOpen(false)}
          onCreated={(id) => {
            setAddPanelOpen(false);
            setSelectedEntryId(id);
          }}
          prefill={addPrefill}
        />
      )}

      {/* Bulk action bar (checkbox selection) */}
      {selectionEnabled && (
        <BulkActionBar
          count={selectedIds.size}
          onDone={() => setSelectedIds(new Set())}
        >
          <BulkActionButton onClick={() => setBulkValuationOpen(true)}>
            Update owed amounts
          </BulkActionButton>
          <BulkActionButton
            onClick={handleBulkExclude}
            tone={bulkExcludeIntent ? "danger" : "default"}
          >
            {bulkExcludeIntent ? "Exclude" : "Re-include"}
          </BulkActionButton>
        </BulkActionBar>
      )}

      <BulkValuationDialog
        open={bulkValuationOpen}
        onOpenChange={setBulkValuationOpen}
        count={selectedIds.size}
        title="Update owed amounts"
        itemNoun="security interest"
        amountLabel="Amount owed ($)"
        submitting={bulkSubmitting}
        onSubmit={handleBulkValuation}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filter select
// ---------------------------------------------------------------------------

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </label>
      <Select
        value={value || "all"}
        onValueChange={(v) => onChange(v === "all" ? "" : v)}
      >
        <SelectTrigger className="h-8 w-full min-w-[130px] justify-between text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="min-w-[160px]">
          {options.map((o) => (
            <SelectItem key={o.value || "all"} value={o.value || "all"}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
